import { Link, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'

import {
  Plus,
  Ticket,
  Lock,
  Percent,
  CurrencyDollar,
  Clock,
  CheckCircle,
  ProhibitInset,
} from '@phosphor-icons/react'

import AppPagination from '../../components/AppPagination/AppPagination'

import merchantApi from '../../services/merchantApi'
import './VouchersPage.scss'
import Header from '../../components/Header/Header'
import { X } from 'lucide-react'
import { CircularProgress } from '@mui/material'
import { Icon } from '@iconify/react/dist/iconify.js'
import AppTable from '../../components/AppTable/AppTable'
import FormEditors from '../../components/FormEditors/FormEditors'

const moduleCode = 'voucher'

const money = (value) => `RM${Number(value || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const number = (value) => Number(value || 0).toLocaleString('en-MY')

const unwrapList = (response) => {
  const payload = response?.data ?? response
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.records)) return payload.records
  return []
}

const normaliseStatus = (value) => {
  if (value === 1 || value === true || value === '1') return 'active'
  if (value === 0 || value === false || value === '0') return 'inactive'
  return String(value || 'active').toLowerCase()
}

const discountLabel = (item) => {
  const type = item.type || (item.discount_percentage ? 'percentage' : 'amount')
  const value = item.discount_value ?? item.discount_amount ?? item.discount_percentage ?? 0
  return type === 'percentage' || type === 'percent' ? `${Number(value || 0)}% off` : `${money(value)} off`
}

const dateLabel = (start, end) => {
  if (!start && !end) return 'No expiry'
  const format = (value) => value ? new Date(value).toLocaleDateString('en-GB') : 'Anytime'
  return `${format(start)} - ${format(end)}`
}

const daysRemaining = (end) => {
  if (!end) return null
  const diff = new Date(end).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)
  return Math.max(0, Math.ceil(diff / 86400000))
}

const extractOutletList = (response) => {
  const payload = response?.data ?? response
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.outlets)) return payload.outlets
  if (Array.isArray(payload?.items)) return payload.items
  return []
}

const resolveStartDate = (item) => item.start_at || item.start || item.valid_from || item.start_date || null
const resolveEndDate = (item) => item.end_at || item.end || item.valid_until || item.end_date || null
const toInputDate = (value) => {
  if (!value) return ''
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return ''
  return parsed.toISOString().slice(0, 10)
}

const mapRewardVoucher = (item) => {
  const claimed = Number(item.claimed_count ?? item.redeemed_count ?? item.used_count ?? 0)
  const total = Number(item.quantity ?? item.total ?? item.limit ?? 0) || 0
  const discount = discountLabel(item)
  return {
    raw: item,
    id: item.id,
    title: item.name || item.title || `Voucher #${item.id}`,
    voucherName: item.name || item.title || `Voucher #${item.id}`,
    points: `${number(item.points_required ?? item.points_needed)} pts`,
    pointsNum: Number(item.points_required ?? item.points_needed ?? 0),
    minimum: item.has_min_spend || item.min_spend ? `Min ${money(item.min_spend)}` : 'No min spend',
    validity: dateLabel(resolveStartDate(item), resolveEndDate(item)),
    validityLong: dateLabel(resolveStartDate(item), resolveEndDate(item)),
    daysRemaining: daysRemaining(resolveEndDate(item)),
    claimed,
    total,
    left: Math.max(0, total - claimed),
    revenue: money(item.revenue ?? item.total_revenue ?? 0),
    type: (item.type === 'percentage' || item.type === 'percent') ? 'percent' : 'cash',
    discount,
    scope: item.outlet_id ? `Outlet #${item.outlet_id}` : 'Merchant-wide',
    status: normaliseStatus(item.status),
  }
}

const mapPromoCode = (item) => {
  const redeemed = Number(item.redeemed_count ?? item.used_count ?? item.claimed_count ?? 0)
  const total = Number(item.quantity ?? item.total ?? item.limit ?? 0) || 0
  const discount = discountLabel(item)
  return {
    raw: item,
    id: item.id,
    title: item.code || item.name || `Promo #${item.id}`,
    code: item.code || item.name || `PROMO${item.id}`,
    description: item.name || item.description || discount,
    discount,
    discountBadge: discount,
    minimum: item.has_min_spend || item.min_spend ? `Min ${money(item.min_spend)}` : 'No min spend',
    validity: dateLabel(resolveStartDate(item), resolveEndDate(item)),
    validityLong: dateLabel(resolveStartDate(item), resolveEndDate(item)),
    daysRemaining: daysRemaining(resolveEndDate(item)),
    redeemed,
    total,
    left: Math.max(0, total - redeemed),
    revenue: money(item.revenue ?? item.total_revenue ?? 0),
    type: (item.type === 'percentage' || item.type === 'percent') ? 'percent' : 'cash',
    scope: item.scope === 'outlet_specific' ? item?.outlets?.map((o) => `${o.name || `Outlet #${o.id}`}`).join(', ') : 'All',
    status: normaliseStatus(item.status),
  }
}

function VoucherCard({ item, kind, onClick }) {
  const isVoucher = kind === 'voucher'
  const claimed = isVoucher ? item.claimed : item.redeemed
  const total = item.total || 0
  const pct = total ? Math.round((claimed / total) * 100) : 0

  return (
    <div className="voucher-card" onClick={onClick} style={{ cursor: 'pointer' }}>
      <div className="voucher-card__top">
        <div className="voucher-card__icon">
          {item.type === 'percent' ? <Percent size={24} /> : <CurrencyDollar size={24} />}
        </div>
        <div className={`voucher-status ${item.status}`}><Icon icon="ph:circle-fill" height={5} /> {item.status.charAt(0).toUpperCase() + item.status.slice(1)}</div>
      </div>

      <h3>{isVoucher ? item.title : item.code}</h3>

      <div className="voucher-meta">
        {isVoucher ? (
          <><span>{item.points}</span><span>•</span><span>{item.minimum}</span><span>•</span><span>{item.validity}</span></>
        ) : (
          <><span>{item.discount}</span><span>•</span><span>{item.minimum}</span><span>•</span><span>{item.validity}</span></>
        )}
      </div>

      <div className="voucher-progress">
        <div className="voucher-progress__bar" style={{ width: `${pct}%` }} />
      </div>

      <div className="voucher-progress__footer">
        <span>{claimed}/{total || '∞'} claimed</span>
        <span>{total ? item.left : 'Unlimited'} left</span>
      </div>
    </div>
  )
}

function OutletApplicabilityTab({ item, outletList }) {
  const selectedOutletIds = new Set((item?.raw?.outlets || []).map((outlet) => String(outlet.id)))
  const sourceOutlets = Array.isArray(outletList) && outletList.length
    ? outletList
    : (item?.raw?.outlets || [])

  const displayOutlets = sourceOutlets.map((outlet) => {
    const outletId = String(outlet.id)
    const included = selectedOutletIds.has(outletId)
    return {
      id: outletId,
      name: outlet.name || `Outlet #${outletId}`,
      address: outlet.address || '-',
      included,
      redeemed_count: included ? Number(item?.raw?.outlets?.find((o) => String(o.id) === outletId)?.redeemed_count || item?.raw?.outlets?.find((o) => String(o.id) === outletId)?.claimed_count || 0) : 0,
    }
  })
  console.log("check this",item?.raw?.scope)
  const selectedCount = displayOutlets.filter((outlet) => outlet.included).length
  return (
    <div className="vdp-outlet-tab">
      {
        item?.raw?.scope !== 'merchant_wide' ?
          <p className="vdp-outlet-tab__count">{selectedCount} of {displayOutlets.length} outlets selected</p>
          : <p className="vdp-outlet-tab__count">This voucher is available at all outlets</p>
      }

      <div className="vdp-outlet-list">
        {displayOutlets.map((outlet) => (
          <div key={outlet.id} className={`vdp-outlet-row ${!outlet.included && item?.raw?.scope !== 'merchant_wide' ? 'vdp-outlet-row--excluded' : ''}`}>
            <div>
              <p className="vdp-outlet-row__name">{outlet.name}</p>
              <p className="vdp-outlet-row__address">{outlet.address}</p>
            </div>
            {
              item?.raw?.scope !== 'merchant_wide' ?
                outlet.included
                  ? <span className="vdp-outlet-row__redeemed">{outlet.redeemed_count} redeemed</span>
                  : <span className="vdp-outlet-row__not-included">Not included</span>
                : ''
            }

          </div>
        ))}
      </div>
    </div>
  )
}


function FormGroup({ label, hint, optional, error, children }) {
  return (
    <div className="vb-field">
      {label && <label className="vb-field__label">{label}{optional && <span className="vb-field__optional">optional</span>}</label>}
      {children}
      {error && <span className="vb-field__error">{error}</span>}
      {hint && !error && <p className="vb-field__hint">{hint}</p>}
    </div>
  )
}

function PrefixInput({ prefix, suffix, value, onChange, placeholder, disabled, error }) {
  return (
    <div className={`vb-prefix-input ${error ? 'vb-prefix-input--error' : ''} ${disabled ? 'vb-prefix-input--disabled' : ''}`}>
      {prefix && <span className="vb-prefix-input__tag">{prefix}</span>}
      <input type="text" inputMode="decimal" value={value} onChange={onChange} placeholder={placeholder} disabled={disabled} />
      {suffix && <span className="vb-prefix-input__suffix">{suffix}</span>}
    </div>
  )
}

function VoucherDetailsTab({ item, isVoucher, detailForm, setDetailForm, outletList, orderTypeList }) {
  const raw = item?.raw || {}
  const discountType = detailForm.discount_type || raw.discount_type || raw.type || 'amount'
  const isPercentageDiscount = discountType === 'percentage_off' || discountType === 'percentage' || discountType === 'percent'
  const scope = detailForm.scope || raw.scope || 'merchant_wide'

  const selectedOutlets = Array.isArray(detailForm.outlet_ids) ? detailForm.outlet_ids : []

  const updateForm = (field, value) => {
    setDetailForm((prev) => ({ ...prev, [field]: value }))
  }

  const handleNumberInput = (field, value) => {
    if (/^\d*\.?\d*$/.test(value)) {
      updateForm(field, value)
    }
  }

  const formatOrderTypeLabel = (value) =>
    String(value || '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase())

  return (
    <div className="vdp-detail-form">
      <p className="vdp-detail-form__section-title">Voucher Type & Discount</p>

      <FormGroup label="Name">
        <input className="vb-input" value={detailForm.name || ''} onChange={(e) => updateForm('name', e.target.value)} />
      </FormGroup>

      <FormGroup label="Discount type">
        <div className="vdp-detail-form__option-grid">
          <button type="button" className={`vdp-detail-form__option ${isPercentageDiscount ? 'vdp-detail-form__option--active' : ''}`} onClick={() => updateForm('discount_type', 'percentage_off')}>
            <span className="vdp-detail-form__option-radio" />
            <div>
              <strong>Percentage off</strong>
              <p>A % discount on the order subtotal.</p>
            </div>
          </button>
          <button type="button" className={`vdp-detail-form__option ${!isPercentageDiscount ? 'vdp-detail-form__option--active' : ''}`} onClick={() => updateForm('discount_type', 'fixed_amount_off')}>
            <span className="vdp-detail-form__option-radio" />
            <div>
              <strong>Fixed amount off</strong>
              <p>A fixed RM value deducted from the order total.</p>
            </div>
          </button>
        </div>
      </FormGroup>

      <div className="vb-form-row">
        <FormGroup label={isPercentageDiscount ? 'Discount percentage' : 'Discount amount'} hint="Applied to order subtotal before tax">
          <PrefixInput
            prefix={isPercentageDiscount ? undefined : 'RM'}
            suffix={isPercentageDiscount ? '%' : undefined}
            value={detailForm.fixed_amount || ''}
            placeholder="0"
            onChange={(e) => handleNumberInput('fixed_amount', e.target.value)}
          />
        </FormGroup>
        <FormGroup label="Max discount cap" optional hint="Limits the max RM saved per redemption">
          <PrefixInput
            prefix="RM"
            placeholder="No cap"
            value={detailForm.max_discount_cap || ''}
            onChange={(e) => handleNumberInput('max_discount_cap', e.target.value)}
          />
        </FormGroup>
      </div>

      {isVoucher && (
        <FormGroup label="Points required">
          <PrefixInput
            value={detailForm.points_required || ''}
            placeholder="0"
            onChange={(e) => handleNumberInput('points_required', e.target.value)}
          />
        </FormGroup>
      )}

      <FormGroup label="Terms and conditions">
        <FormEditors
          value={detailForm.terms}
          setValue={(value) => updateForm('terms', value)}
          placeholder="Enter terms and conditions here..."
          maxWords={200}
        />
      </FormGroup>

      <p className="vdp-detail-form__section-title">Scope & Usage Rules</p>

      <FormGroup label="Scope">
        <div className="vdp-detail-form__option-grid">
          <button type="button" className={`vdp-detail-form__option ${scope === 'merchant_wide' ? 'vdp-detail-form__option--active' : ''}`} onClick={() => updateForm('scope', 'merchant_wide')}>
            <span className="vdp-detail-form__option-radio" />
            <div>
              <strong>Merchant-wide</strong>
              <p>Available at all outlets.</p>
            </div>
          </button>
          <button type="button" className={`vdp-detail-form__option ${scope === 'outlet_specific' ? 'vdp-detail-form__option--active' : ''}`} onClick={() => updateForm('scope', 'outlet_specific')}>
            <span className="vdp-detail-form__option-radio" />
            <div>
              <strong>Outlet specific</strong>
              <p>Available only at selected outlets.</p>
            </div>
          </button>
        </div>
      </FormGroup>

      {scope === 'outlet_specific' && (
        <FormGroup label="Selected outlets">
          <div className="vdp-detail-form__chips">
            {(outletList || []).length
              ? (outletList || []).map((outlet) => {
                const isSelected = selectedOutlets.includes(outlet.id)
                return (
                  <button
                    type="button"
                    key={outlet.id}
                    className={`vdp-detail-form__chip ${isSelected ? 'vdp-detail-form__chip--active' : ''}`}
                    onClick={() => {
                      const next = isSelected
                        ? selectedOutlets.filter((id) => id !== outlet.id)
                        : [...selectedOutlets, outlet.id]
                      updateForm('outlet_ids', next)
                    }}
                  >
                    {outlet.name}
                  </button>
                )
              })
              : <span className="vdp-detail-form__empty">No selected outlets</span>}
          </div>
        </FormGroup>
      )}

      <div className="vb-form-row">
        <FormGroup label="Total claim limit" optional>
          <PrefixInput
            value={detailForm.total_claim_limit || ''}
            placeholder="Unlimited"
            onChange={(e) => handleNumberInput('total_claim_limit', e.target.value)}
          />
        </FormGroup>
        <FormGroup label="Usage limit per customer" optional>
          <PrefixInput
            value={detailForm.usage_limit_per_customer || ''}
            placeholder="Unlimited"
            onChange={(e) => handleNumberInput('usage_limit_per_customer', e.target.value)}
          />
        </FormGroup>
      </div>

      <div className="vb-form-row">
        <FormGroup label="Minimum order spend" optional>
          <PrefixInput
            prefix="RM"
            value={detailForm.minimum_order_spend || ''}
            placeholder="None"
            onChange={(e) => handleNumberInput('minimum_order_spend', e.target.value)}
          />
        </FormGroup>
        <FormGroup label="Valid for order types">
          <div className="vdp-detail-form__chips">
            {
              orderTypeList?.length > 0 ? orderTypeList.map((item) =>
                <button
                  key={item?.value}
                  type="button"
                  className={`vb-chip ${detailForm.valid_order_types.includes(item?.value) ? 'vb-chip--active' : ''}`}
                  onClick={() => updateForm('valid_order_types', detailForm.valid_order_types.includes(item?.value) ? detailForm.valid_order_types.filter((value) => value !== item?.value) : [...detailForm.valid_order_types, item?.value])}>
                  {item?.label}
                </button>
              )
                : <p className="vb-outlet-picker__empty">No order types found</p>
            }
          </div>
        </FormGroup>
      </div>

      <div className="vb-form-row">
        <FormGroup label="Valid from">
          <input className="vb-input" type="date" value={detailForm.valid_from || ''} onChange={(e) => updateForm('valid_from', e.target.value)} />
        </FormGroup>
        <FormGroup label="Valid until">
          <input className="vb-input" type="date" value={detailForm.valid_until || ''} onChange={(e) => updateForm('valid_until', e.target.value)} />
        </FormGroup>
      </div>
    </div>
  )
}

function DetailsPanel({ item, kind, onClose, onDelete, onToggleStatus, onSaveDetails, busy, loading, outletList, orderTypeList, editable, deletable }) {
  const isVoucher = kind === 'voucher'
  const isActive = item.status === 'active'
  const claimed = isVoucher ? item.claimed : item.redeemed
  const total = item.total || 0
  const pct = total ? Math.round((claimed / total) * 100) : 0

  const [activeTab, setActiveTab] = useState('outlet_applicability')
  const [detailForm, setDetailForm] = useState({
    name: '',
    terms: '',
    discount_type: 'fixed_amount_off',
    fixed_amount: '',
    max_discount_cap: '',
    points_required: '',
    scope: 'merchant_wide',
    outlet_ids: [],
    total_claim_limit: '',
    usage_limit_per_customer: '',
    minimum_order_spend: '',
    valid_order_types: [],
    valid_from: '',
    valid_until: '',
    action: 'publish',
  })

  useEffect(() => {
    const raw = item?.raw || {}
    setDetailForm({
      name: raw.name || item?.voucherName || item?.title || '',
      terms: raw.t_and_c || raw.terms_and_conditions || '',
      discount_type: raw.discount_type || ((raw.type === 'percentage' || raw.type === 'percent') ? 'percentage_off' : 'fixed_amount_off'),
      fixed_amount: String(raw.fixed_amount ?? raw.discount_amount ?? raw.discount_value ?? ''),
      max_discount_cap: String(raw.max_discount_cap ?? raw.max_discount_value ?? ''),
      points_required: String(raw.points_required ?? raw.points_needed ?? ''),
      scope: raw.scope || 'merchant_wide',
      outlet_ids: Array.isArray(raw.outlets) ? raw.outlets.map((outlet) => outlet.id) : [],
      total_claim_limit: String(raw.total_claim_limit ?? raw.quantity ?? ''),
      usage_limit_per_customer: String(raw.usage_limit_per_customer ?? raw.limit ?? ''),
      minimum_order_spend: String(raw.minimum_order_spend ?? raw.min_spend ?? ''),
      valid_order_types: Array.isArray(raw.valid_order_types) ? raw.valid_order_types : [],
      valid_from: toInputDate(resolveStartDate(raw)),
      valid_until: toInputDate(resolveEndDate(raw)),
      action: 'publish',
    })
    setActiveTab('outlet_applicability')
  }, [item?.id, kind])

  return (
    <>
      <div className="vdp-overlay" onClick={onClose} />
      <aside className="vdp-panel">
        <div className="vdp-panel__topbar">
          <span>Voucher details</span>
          <button type="button" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="vdp-panel__hero">
          <div>
            <p className="vdp-panel__hero-kind">{isVoucher ? 'Reward Voucher' : 'Promo code'}</p>
            <h2 className="vdp-panel__hero-title">{isVoucher ? item.title : item.code}</h2>
          </div>
          <div className="vdp-panel__hero-badges">
            <span className={`vdp-badge vdp-badge--${kind === 'promo' ? 'promo' : 'discount'}`}><Icon icon="ph:circle-fill" height={5} /> {item.discount || item.discountBadge}</span>
            <span className={`voucher-status ${item.status}`}><Icon icon="ph:circle-fill" height={5} /> {item.status.charAt(0).toUpperCase() + item.status.slice(1)}</span>
          </div>
        </div>

        <div className="vdp-panel__body">
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px', width: '100%' }}>
              <CircularProgress style={{ color: '#053F48' }} />
            </div>
          ) : (
            <>
              <div className="vdp-meta">
                {isVoucher ? (
                  <>
                    <div className="vdp-meta__row"><span>Voucher name:</span><strong>{item.voucherName}</strong></div>
                    <div className="vdp-meta__row"><span>Points required:</span><strong>{item.pointsNum} pts</strong></div>
                  </>
                ) : (
                  <>
                    <div className="vdp-meta__row"><span>Promo code:</span><strong>{item.code}</strong></div>
                    <div className="vdp-meta__row"><span>Description:</span><strong>{item.description}</strong></div>
                  </>
                )}
                <div className="vdp-meta__row"><span>Discount:</span><strong>{item.discount}</strong></div>
                <div className="vdp-meta__row"><span>Scope:</span><strong>{item.scope}</strong></div>
                <div className="vdp-meta__row">
                  <span>Validity:</span>
                  <strong>{item.validityLong}</strong>
                  {item.daysRemaining !== null && item.daysRemaining > 0 && (
                    <span className="vdp-meta__remaining"><Clock size={14} /> {item.daysRemaining} days remaining</span>
                  )}
                </div>
              </div>

              <p className="vdp-section-label">FOR ALL TIME</p>
              <div className="vdp-stats-card">
                <div className="vdp-stats-card__row">
                  <div><span>USAGE LIMIT</span><strong>{claimed} <span className="vdp-stats-card__of">/{total || '∞'}</span></strong></div>
                  <div><span>REVENUE</span><strong>{item.revenue}</strong></div>
                </div>
                <div className="voucher-progress voucher-progress__modal-bar "><div className="voucher-progress__bar" style={{ width: `${pct}%` }} /></div>
                <div className="voucher-progress__footer"><span>{claimed}/{total || '∞'} claimed</span><span>{total ? `${item.left} remaining` : 'Unlimited'}</span></div>
              </div>

              <div className="voucher-tabs">
                <button className={activeTab === 'outlet_applicability' ? 'active' : ''} onClick={() => setActiveTab('outlet_applicability')}>Outlet Applicability</button>
                <button className={activeTab === 'outlet_details' ? 'active' : ''} onClick={() => setActiveTab('outlet_details')}>Details</button>
              </div>

              {activeTab === 'outlet_applicability' && <OutletApplicabilityTab item={item} outletList={outletList} />}
              {activeTab === 'outlet_details' && <VoucherDetailsTab item={item} isVoucher={isVoucher} detailForm={detailForm} setDetailForm={setDetailForm} outletList={outletList} orderTypeList={orderTypeList} />}
            </>
          )}
        </div>

        {!loading && (
          <div className="vdp-panel__footer">
            <div className="vdp-panel__footer-actions">
              {
                editable &&
                <button className="vdp-footer-delete" onClick={onDelete} disabled={busy || loading}>
                  <Icon icon="ph:trash" height={16}></Icon>
                  Delete
                </button>
              }
              {
                editable &&
                <button className={`vdp-footer-toggle ${isActive ? 'vdp-footer-toggle--deactivate' : 'vdp-footer-toggle--activate'}`} onClick={onToggleStatus} disabled={busy || loading}>
                  {isActive ? <><ProhibitInset size={16} /> Deactivate</> : <><CheckCircle size={16} weight="fill" /> Activate</>}
                </button>
              }
            </div>

            {activeTab === 'outlet_details' && !loading ? (
              <button type="button" className="vdp-panel__save-btn" onClick={() => onSaveDetails?.(detailForm)} disabled={busy}>Save</button>
            ) : (
              <button type="button" className="vdp-panel__save-btn" onClick={onClose}>Close</button>
            )}
          </div>
        )}
      </aside>
    </>
  )
}

function DeleteVoucherModal({ open, item, kind, busy, onClose, onConfirm }) {
  if (!open || !item) return null

  const expiryRaw = resolveEndDate(item.raw || {})
  const expiryLabel = expiryRaw
    ? new Date(expiryRaw).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '-'

  const codeOrName = kind === 'promo' ? item.code : item.voucherName
  const subline = `${item.discount || '-'} • expires ${expiryLabel}`

  return (
    <>
      <div className="vdp-delete-backdrop" onClick={onClose} />
      <div className="vdp-delete-modal" role="dialog" aria-modal="true" aria-labelledby="vdp-delete-modal-title">
        <div className="vdp-delete-modal__header">
          <h3 id="vdp-delete-modal-title">Delete {kind === 'promo' ? 'promo code' : 'voucher'}</h3>
          <button type="button" className="vdp-delete-modal__close" onClick={onClose} disabled={busy}>
            <X size={20} />
          </button>
        </div>

        <div className="vdp-delete-modal__body">
          <p className="vdp-delete-modal__desc">This action is permanent and cannot be undone.</p>

          <div className="vdp-delete-modal__card">
            <div className="vdp-delete-modal__card-info">
              <div className="vdp-delete-modal__card-icon">
                <Icon icon="ph:ticket" height={20} />
              </div>
              <div>
                <p className="vdp-delete-modal__card-name">{codeOrName}</p>
                <p className="vdp-delete-modal__card-sub">{subline}</p>
              </div>
            </div>
            <span className={`voucher-status ${item.status}`}><Icon icon="ph:circle-fill" height={5} /> {item.status.charAt(0).toUpperCase() + item.status.slice(1)}</span>
          </div>

          <ul className="vdp-delete-modal__consequences">
            <li>The {kind === 'promo' ? 'promo code' : 'voucher code'} will stop working immediately.</li>
            <li>All usage history will be permanently erased.</li>
            <li>Customers with this code can no longer redeem it.</li>
          </ul>
        </div>

        <div className="vdp-delete-modal__footer">
          <button type="button" className="vdp-delete-modal__cancel" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="vdp-delete-modal__confirm" onClick={onConfirm} disabled={busy}>
            <Icon icon="ph:trash" height={16} />
            {busy ? 'Deleting...' : `Delete ${kind === 'promo' ? 'promo code' : 'voucher'}`}
          </button>
        </div>
      </div>
    </>
  )
}

export default function VouchersPage() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('reward')
  const [rewardVouchers, setRewardVouchers] = useState([])
  const [promoCodes, setPromoCodes] = useState([])
  const [selectedItem, setSelectedItem] = useState(null)
  const [selectedKind, setSelectedKind] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [error, setError] = useState('')
  const [userInfo, setUserInfo] = useState(null)
  const [voucherMeta, setVoucherMeta] = useState(null)
  const [promoMeta, setPromoMeta] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [outletList, setOutletList] = useState([])
  const [orderTypeList, setOrderTypeList] = useState([])
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedOutletId, setSelectedOutletId] = useState(null)

  //permission states
  const [creatable, setCreatable] = useState(true)
  const [editable, setEditable] = useState(true)
  const [deletable, setDeletable] = useState(true)

  const loadData = async () => {
    setLoading(true)
    setError('')
    try {
      const [rewardResponse, promoResponse] = await Promise.all([
        merchantApi.vouchers.reward.list({ "filter[outlet_id]": selectedOutletId }),
        merchantApi.vouchers.promo.list({ "filter[outlet_id]": selectedOutletId }),
      ])

      setRewardVouchers(unwrapList(rewardResponse).map(mapRewardVoucher))
      setVoucherMeta(rewardResponse?.data || null)

      setPromoCodes(unwrapList(promoResponse).map(mapPromoCode))
      setPromoMeta(promoResponse?.data || null)
    } catch (err) {
      setError(err.message || 'Unable to load vouchers')
    } finally {
      setLoading(false)
    }
  }

  const loadUsers = async () => {

    try {
      const response = await merchantApi.auth.me()
      if (!response?.data?.permissions?.includes(`view-${moduleCode}`)) {
        navigate('/unauthorized')
        return
      }
      const generalInfo = await merchantApi.general.systemInfo()
      setUserInfo(response?.data || null);
      setCreatable(response?.data?.permissions?.includes(`create-${moduleCode}`))
      setEditable(response?.data?.permissions?.includes(`edit-${moduleCode}`))
      setSelectedOutletId(response?.data?.default_outlet?.id || null)
      // setDeletable(response?.data?.permissions?.includes(`delete-${moduleCode}`))
      setOrderTypeList(generalInfo?.data?.order_types || [])
    } catch (err) {
      setError(err?.message || 'Failed to load user info.')
      setUserInfo(null)
      setOrderTypeList([])
    } finally {
      setLoading(false)
    }
  }

  const loadOutlets = async () => {

    try {
      const response = await merchantApi.outlets.list({ page: 0 })
      setOutletList(extractOutletList(response))
    } catch (err) {
      setError(err?.message || 'Failed to load outlet list.')
      setOutletList([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { if (selectedOutletId) loadData(); }, [currentPage, selectedOutletId])
  useEffect(() => { loadUsers(); loadOutlets(); }, [])
  const currentMeta = activeTab === 'reward' ? voucherMeta : promoMeta
  const currentItems = activeTab === 'reward' ? rewardVouchers : promoCodes
  const activeCount = currentMeta?.summary?.active_vouchers || currentMeta?.summary?.active_promo_codes || 0
  const redeemedCount = currentMeta?.summary?.redeemed_this_month || 0
  const pointsSpent = currentMeta?.summary?.points_spent || 0
  const redemptionRate = currentMeta?.summary?.redemption_rate_label || 0

  const openPanel = (item, kind) => {
    setDetailsLoading(true)
    setSelectedItem(item)
    setSelectedKind(kind)

    const request = kind === 'voucher'
      ? merchantApi.vouchers.reward.show(item.id)
      : merchantApi.vouchers.promo.show(item.id)

    request
      .then((response) => {
        const detail = response?.data?.data || response?.data || response
        if (!detail) return
        const mapped = kind === 'voucher' ? mapRewardVoucher(detail) : mapPromoCode(detail)
        setSelectedItem(mapped)
      })
      .catch((err) => {
        setError(err?.message || 'Failed to load voucher details.')
      })
      .finally(() => {
        setDetailsLoading(false)
      })
  }

  const toggleStatus = async () => {
    if (!selectedItem || !selectedKind) return
    const nextStatus = selectedItem.status === 'active' ? 0 : 1
    setBusy(true)
    try {
      if (selectedKind === 'voucher') await merchantApi.vouchers.reward.updateStatus(selectedItem.id, { status: nextStatus })
      else await merchantApi.vouchers.promo.updateStatus(selectedItem.id, { status: nextStatus })
      await loadData()
      setSelectedItem((prev) => prev ? { ...prev, status: nextStatus ? 'active' : 'inactive' } : prev)
    } catch (err) {
      setError(err.message || 'Unable to update status')
    } finally {
      setBusy(false)
    }
  }

  const deleteItem = async () => {
    if (!selectedItem || !selectedKind) return
    setBusy(true)
    try {
      if (selectedKind === 'voucher') await merchantApi.vouchers.reward.remove(selectedItem.id)
      else await merchantApi.vouchers.promo.remove(selectedItem.id)
      setShowDeleteModal(false)
      setSelectedItem(null)
      setSelectedKind(null)
      await loadData()
    } catch (err) {
      setError(err.message || 'Unable to delete item')
    } finally {
      setBusy(false)
    }
  }

  const saveDetails = async (formValues) => {
    if (!selectedItem || !selectedKind) return

    const toNumberOrNull = (value) => {
      if (value === '' || value === null || value === undefined) return null
      const parsed = Number(value)
      return Number.isFinite(parsed) ? parsed : null
    }

    const payload = {
      name: formValues.name?.trim(),
      discount_type: formValues.discount_type,
      fixed_amount: formValues.discount_type === 'fixed_amount_off' ? toNumberOrNull(formValues.fixed_amount) : null,
      discount_percentage: formValues.discount_type === 'percentage_off' ? toNumberOrNull(formValues.fixed_amount) : null,
      max_discount_cap: toNumberOrNull(formValues.max_discount_cap),
      t_and_c: formValues.terms?.trim() || null,
      scope: formValues.scope,
      outlet_ids: formValues.scope === 'outlet_specific' ? formValues.outlet_ids : [],
      total_claim_limit: toNumberOrNull(formValues.total_claim_limit),
      usage_limit_per_customer: toNumberOrNull(formValues.usage_limit_per_customer),
      minimum_order_spend: toNumberOrNull(formValues.minimum_order_spend),
      valid_order_types: formValues.valid_order_types || [],
      valid_from: formValues.valid_from || null,
      valid_until: formValues.valid_until || null,
      action: 'publish',
    }

    if (selectedKind === 'voucher') {
      payload.points_required = toNumberOrNull(formValues.points_required)
    }

    setBusy(true)
    try {
      if (selectedKind === 'voucher') {
        await merchantApi.vouchers.reward.update(selectedItem.id, payload)
      } else {
        await merchantApi.vouchers.promo.update(selectedItem.id, payload)
      }

      const detailResponse = selectedKind === 'voucher'
        ? await merchantApi.vouchers.reward.show(selectedItem.id)
        : await merchantApi.vouchers.promo.show(selectedItem.id)

      const detail = detailResponse?.data?.data || detailResponse?.data || detailResponse
      const mapped = selectedKind === 'voucher' ? mapRewardVoucher(detail) : mapPromoCode(detail)
      // close the details modal
      setSelectedItem(null)
      setSelectedKind(null)
      await loadData()
    } catch (err) {
      setError(err?.message || 'Unable to save voucher details')
    } finally {
      setBusy(false)
    }
  }

  const merchantWideVouchers = currentItems.filter((item) => item?.raw?.scope === "merchant_wide")
  const outletSpecificVouchers = currentItems.filter((item) => item?.raw?.scope === "outlet_specific")

  const columns = [
    {
      field: 'code', header: 'Code'
    },
    {
      field: 'discount', header: 'Discount',
      render: (row) => (
        <span className="vdp-badge vdp-badge--promo"><Icon icon="ph:circle-fill" height={5} /> {row.discount || row.discountBadge}</span>
      ),
    },
    {
      field: 'scope', header: 'Scope'
    },
    {
      field: 'claimed_count', header: 'Used',
      render: (row) => (<>{row.claimed_count || 0}</>)
    },
    {
      field: 'validity', header: 'Validity',
      render: (row) => {
        console.log('row.valid_from', row)
        return <>{row.validity || ''}</>
      }
    },
    {
      field: 'status', header: 'Status',
      render: (row) => (
        <div className={`voucher-status ${row.status}`}><Icon icon="ph:circle-fill" height={5} /> {row.status.charAt(0).toUpperCase() + row.status.slice(1)}</div>
      ),
    },
    {
      field: 'actions', header: '',
      render: (row) => (
        <div className="vdp-action-icon-container ">
          <button className="orders-table__menu-button" onClick={(e) => openPanel(row, 'promo')}>
            <Icon icon={"ph:pencil-simple"} height={16} />
          </button>
          <button className="orders-table__menu-button" onClick={(e) => { setSelectedItem(row); setSelectedKind('promo'); setShowDeleteModal(true); }}>
            <Icon icon={"ph:trash"} height={16} />
          </button>
        </div>
      ),
    },
  ]
  console.log("selectedItem", selectedItem)
  return (
    <div className="vouchers-page">
      <Header
        items={[
          { label: userInfo?.merchant?.business_name || 'Merchant', to: '/' },
          { label: 'Vouchers', current: true },
        ]}
      />
      <div className="page-header">
        <div>
          <div className="vouchers-title">Vouchers</div>
          <div className="vouchers-subtitle">Configure reward vouchers redeemed with loyalty points and customer-entered promo codes</div>
        </div>
      </div>

      <div className="voucher-hero">
        <div className="voucher-hero__card reward">
          <div className="voucher-hero__top">
            <div className="voucher-hero__icon reward"><Ticket size={28} /></div>
            <div><h3>Reward vouchers</h3><p>Redeemed with loyalty points — no code needed</p></div>
          </div>
          <div className="voucher-hero__description">Customers tap to claim from their loyalty wallet using earned points. Two discount types: % off subtotal or fixed RM off total.</div>
          {
            creatable &&
            <Link to="/vouchers/create-reward" className="voucher-overview-card__button"><Plus size={16} /> Create reward voucher</Link>
          }
        </div>

        <div className="voucher-hero__card promo">
          <div className="voucher-hero__top">
            <div className="voucher-hero__icon promo"><Lock size={28} /></div>
            <div><h3>Promo codes</h3><p>Entered manually at checkout</p></div>
          </div>
          <div className="voucher-hero__description">Customers manually enter an alphanumeric code (e.g. SAKURA10) in the promo field at checkout. Two types: % off or fixed RM off.</div>
          {
            creatable &&
            <Link to="/vouchers/create-promo" className="voucher-overview-card__button voucher-overview-card__button--purple"><Plus size={16} /> Create promo code</Link>
          }
        </div>
      </div>

      <div className="voucher-tabs">
        <button className={activeTab === 'reward' ? 'active' : ''} onClick={() => setActiveTab('reward')}>Reward vouchers ({rewardVouchers.length})</button>
        <button className={activeTab === 'promo' ? 'active' : ''} onClick={() => setActiveTab('promo')}>Promo codes ({promoCodes.length})</button>
      </div>

      {error && <div className="api-error">{error}</div>}
      {loading ? (
        <div className="loading-state">Loading vouchers...</div>
      ) : (
        <>
          <div className="voucher-stats">
            <div className="vouchers-stat-card"><span>ACTIVE {activeTab === 'reward' ? 'VOUCHERS' : 'PROMO CODES'}</span><h3>{activeCount}</h3></div>
            <div className="vouchers-stat-card"><span>REDEEMED THIS MONTH</span><h3>{number(redeemedCount)}</h3></div>
            {activeTab === 'reward' && <div className="vouchers-stat-card"><span>POINTS SPENT</span><h3>{number(pointsSpent)} pts</h3></div>}
            {activeTab === 'promo' && <div className="vouchers-stat-card"><span>REDEMPTION RATE</span><h3>{redemptionRate}</h3></div>}
          </div>
          {
            activeTab === 'reward' &&
            <>
              <div className="voucher-section-title">MERCHANT-WIDE</div>
              <div className="voucher-grid">
                {
                  merchantWideVouchers?.length > 0 ? merchantWideVouchers.map((item) => (
                    <VoucherCard key={item.id} item={item} kind={activeTab === 'reward' ? 'voucher' : 'promo'} onClick={() => openPanel(item, activeTab === 'reward' ? 'voucher' : 'promo')} />
                  ))
                    : !merchantWideVouchers?.length && <div className="empty-state">No {activeTab === 'reward' ? 'reward vouchers' : 'promo codes'} found.</div>
                }
              </div>

              <div className="voucher-section-title">Outlet-specific</div>
              <div className="voucher-grid">
                {
                  outletSpecificVouchers?.length > 0 ? outletSpecificVouchers.map((item) => (
                    <VoucherCard key={item.id} item={item} kind={activeTab === 'reward' ? 'voucher' : 'promo'} onClick={() => openPanel(item, activeTab === 'reward' ? 'voucher' : 'promo')} />
                  ))
                    : !outletSpecificVouchers?.length && <div className="empty-state">No {activeTab === 'reward' ? 'reward vouchers' : 'promo codes'} found.</div>
                }
              </div>


            </>
          }
          {
            activeTab === 'promo' &&
            <AppTable
              columns={columns}
              rows={currentItems || []}
              pagination={currentMeta || {}}
              loading={loading}
              onRowClick={(row) => {
                openPanel(row, 'promo')
              }}
              onPageChange={(page) => {
                setCurrentPage(page)
              }}
              noPagination={true}
            />
          }
          {!currentItems.length && <div className="empty-state">No {activeTab === 'reward' ? 'reward vouchers' : 'promo codes'} found.</div>}
          <div className="voucher-pagination">
            <AppPagination
              pagination={currentMeta}
              currentPage={currentPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        </>
      )}

      {selectedItem && selectedKind && (
        <DetailsPanel
          editable={editable}
          deletable={deletable}
          item={selectedItem}
          kind={selectedKind}
          onClose={() => {
            setShowDeleteModal(false)
            setSelectedItem(null)
            setSelectedKind(null)
            setDetailsLoading(false)
          }}
          onDelete={() => setShowDeleteModal(true)}
          onSaveDetails={saveDetails}
          onToggleStatus={toggleStatus}
          busy={busy}
          loading={detailsLoading}
          outletList={outletList}
          orderTypeList={orderTypeList}
        />
      )}

      <DeleteVoucherModal
        open={showDeleteModal}
        item={selectedItem}
        kind={selectedKind}
        busy={busy}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={deleteItem}
      />
    </div>
  )
}
